import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, Link, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ATTR } from './attrs';
import { useHelp } from './HelpContext';
import { FeatureRef, HelpEntry, HelpSectionEntry, HelpTarget, LocalText, pickText, DisabledInfo, RoleDirectory } from './types';
import { currentHelpLang } from './lang';
import { uiText } from './uiText';
import { describeWhy, keysForReason, shortWhy } from './why';
import { useIsMobile } from '../hooks/useIsMobile';
import { humanizeEnum } from '../utils/format';

/** A catalogue text in the current help language (English when it has no translation). */
const txt = (t: LocalText | undefined): string => pickText(t, currentHelpLang());

/** Roles holding every key: one lookup per key, keeping the names found in all of them (in the first key's order). */
async function rolesHoldingAll(dir: RoleDirectory, keys: string[]): Promise<string[]> {
    const lists = await Promise.all(keys.map(k => dir.rolesHolding([k])));
    return lists[0].filter(name => lists.every(l => l.includes(name)));
}

/**
 * Role names that hold any of `keys` (or all of them when `all`); never people.
 * Empty until loaded or if the user may not list roles.
 */
function useHolders(keys: string[], all = false): string[] {
    const { roleDirectory } = useHelp();
    const [holders, setHolders] = useState<string[]>([]);
    const k = keys.join('|');
    useEffect(() => {
        let cancelled = false;
        if (!k) { setHolders([]); return; }
        (all ? rolesHoldingAll(roleDirectory, k.split('|')) : roleDirectory.rolesHolding(k.split('|'))).then(r => { if (!cancelled) setHolders(r); }).catch(() => { if (!cancelled) setHolders([]); });
        return () => { cancelled = true; };
    }, [k, all, roleDirectory]);
    return holders;
}

const Field: React.FC<{ label: string; text?: string }> = ({ label, text }) =>
    text ? (
        <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
            <Typography variant="body2">{text}</Typography>
        </Box>
    ) : null;

const LearnMore: React.FC<{ link?: string }> = ({ link }) =>
    link && /^(https?:\/\/|\/)/.test(link) ? (
        <Link href={link} target="_blank" rel="noopener noreferrer" variant="body2">{uiText('learnMore')}</Link>
    ) : null;

const Access: React.FC<{ entry: HelpEntry; holders: string[] }> = ({ entry, holders }) => {
    const { canUse } = useHelp();
    if (!entry.keys || entry.keys.length === 0) return null;
    const can = canUse(entry.keys, entry.needsAllKeys);
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{uiText('whoCanUse')}</Typography>
            <Typography variant="body2">
                {entry.needsAllKeys && entry.keys.length > 1 ? uiText('needsAllPermissions', { keys: entry.keys.map(k => humanizeEnum(k)).join(', ') }) : ''}
                {holders.length ? uiText('roles', { roles: holders.join(', ') }) : ''}
                <strong>{can ? uiText('youCan') : holders.length ? uiText('youCantAskRole', { role: holders[0] }) : uiText('youCantAskAdmin')}</strong>
            </Typography>
        </Box>
    );
};

const WhyCant: React.FC<{ info: DisabledInfo; entryKeys?: string[]; all?: boolean }> = ({ info, entryKeys, all }) => {
    // A reason that names its own keys is read as any-of; the entry's all-of applies when the reason falls back to the entry's keys.
    const own = info.reason?.kind === 'missing-key' && !!info.reason.keys;
    const holders = useHolders(keysForReason(info, entryKeys), !!all && !own);
    return (
        <Box data-testid="help-why" sx={{ p: 1, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <Typography variant="caption" fontWeight={700}>{uiText('whyCantI')}</Typography>
            <Typography variant="body2">{describeWhy(info, holders)}</Typography>
        </Box>
    );
};

const ElementBody: React.FC<{ t: Extract<HelpTarget, { kind: 'element' }> }> = ({ t }) => {
    const { explainSection } = useHelp();
    const holders = useHolders(t.entry.keys ?? [], t.entry.needsAllKeys);
    return (
        <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>{txt(t.entry.title)}</Typography>
            {t.disabled && <WhyCant info={t.disabled} entryKeys={t.entry.keys} all={t.entry.needsAllKeys} />}
            <Field label={uiText('whatItDoes')} text={txt(t.entry.what)} />
            <Field label={uiText('businessMeaning')} text={txt(t.entry.business)} />
            <Field label={uiText('whatHappensNext')} text={txt(t.entry.next)} />
            <Access entry={t.entry} holders={holders} />
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <LearnMore link={t.entry.link} />
                {t.section && (
                    <Button size="small" onClick={() => explainSection(t.section!)}>{uiText('explainSection')}</Button>
                )}
            </Stack>
        </Stack>
    );
};

const FeatureLine: React.FC<{ f: FeatureRef }> = ({ f }) => {
    const { selectFeature } = useHelp();
    return (
        <Box
            component="button"
            type="button"
            onClick={() => selectFeature(f.el)}
            sx={{ all: 'unset', cursor: 'pointer', display: 'block', py: 0.5, '&:hover': { textDecoration: 'underline' } }}
        >
            <Typography variant="body2" component="span">
                <strong>{txt(f.entry.title)}</strong>: {txt(f.entry.what)}
                {f.disabled && <Chip size="small" label={uiText('unavailable', { why: shortWhy(f.disabled) })} sx={{ ml: 1, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }} />}
            </Typography>
        </Box>
    );
};

const AreaBody: React.FC<{ t: Extract<HelpTarget, { kind: 'area' }> }> = ({ t }) => {
    const s: HelpSectionEntry | undefined = t.section?.entry;
    const holders = useHolders(s?.keys ?? [], s?.needsAllKeys);
    return (
        <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>{s ? txt(s.title) : uiText('thisArea')}</Typography>
            {s && <Field label={uiText('whatThisAreaIsFor')} text={txt(s.what)} />}
            {t.features.length > 0 && (
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{uiText('featuresHere')}</Typography>
                    {t.features.map(f => <FeatureLine key={f.key} f={f} />)}
                </Box>
            )}
            {s && <Field label={uiText('howTheyConnect')} text={txt(s.connects)} />}
            {s && <Field label={uiText('businessMeaning')} text={txt(s.business)} />}
            {s && <Field label={uiText('whatHappensNext')} text={txt(s.next)} />}
            {s && <Access entry={s} holders={holders} />}
            {s && <LearnMore link={s.link} />}
        </Stack>
    );
};

/** Floats next to the highlighted rectangle (or as a bottom sheet on phones). */
export const HelpCard: React.FC<{ anchor: { left: number; top: number; right: number; bottom: number } | null }> = ({ anchor }) => {
    const { target, clearTarget, catalogueError } = useHelp();
    const isMobile = useIsMobile();
    const ref = useRef<HTMLDivElement>(null);
    // A new answer takes keyboard focus (and is announced); the Fab gets focus back when help mode ends.
    useEffect(() => { if (target) ref.current?.focus({ preventScroll: true }); }, [target]);
    if (!target && !catalogueError) return null;

    const W = 340;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    const place: React.CSSProperties = isMobile
        ? { left: 8, right: 8, bottom: 72, maxHeight: '50vh' } // clear of the ? button (bottom right) and the bar (top)
        : (() => {
            const a = anchor;
            const left = a ? Math.min(Math.max(8, a.right + 12 > vw - W - 8 ? a.left - W - 12 : a.right + 12), vw - W - 8) : vw - W - 24;
            const top = a ? Math.min(Math.max(8, a.top), Math.max(8, vh - 320)) : 80;
            return { left: Math.max(8, left), top, width: W, maxHeight: vh - 16 };
        })();

    return (
        <Paper
            elevation={8}
            ref={ref}
            tabIndex={-1}
            role="dialog"
            aria-live="polite"
            aria-label="Help"
            {...{ [ATTR.ui]: '' }}
            sx={{ position: 'fixed', zIndex: 1700, p: 2, overflowY: 'auto', outline: 'none', ...place }}
        >
            <IconButton size="small" aria-label={uiText('closeCard')} onClick={clearTarget} sx={{ position: 'absolute', top: 4, right: 4 }}>
                <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ pr: 3 }}>
                {catalogueError && !target && <Typography variant="body2">{uiText('loadFailed')}</Typography>}
                {target?.kind === 'element' && <ElementBody t={target} />}
                {target?.kind === 'area' && <AreaBody t={target} />}
                {target?.kind === 'missing' && (
                    <Typography variant="body2">{uiText('notWritten')}</Typography>
                )}
            </Box>
        </Paper>
    );
};
